import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GetTokenSilentlyOptions } from '@auth0/auth0-spa-js';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, finalize, mergeMap } from 'rxjs/operators';
import { environment } from '@cde-fe-app/environments/environment';
import { AuthServiceWrapper } from './auth.service';
import * as RootState from '../state';
import * as AuthActions from '../state/auth/auth.actions';

const URL_EXCEPTIONS = [
  // needed when an machine image is changed
  '/stock-images',
  '/individual-images',
  '/image',
  '/operating-instructions',
  '/weather-information',
  ['/dashboards', 'fromOrgReg=1'],
  ['/dashboards', '/widgets'],
  '/inbox/files',
  '/notifications',
  ['/dealers/', (url: string) => url.match(/\d{10}$/)],
  '/map-services/address-search',
  '/registrations/organization',
  '/registrations/form-fields',
];

@Injectable({
  providedIn: 'root',
})
export class InterceptorService implements HttpInterceptor {
  private auth = inject(AuthServiceWrapper);
  private store = inject<Store<RootState.IState>>(Store<RootState.IState>);
  private router = inject(Router);
  private runningRequestUrls = new Set<string>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (
      req.url.includes(environment.apiBaseUrl) ||
      req.url.includes(environment.claasIdApiBaseUrl) ||
      req.url.includes(environment.claasIdUserApiBaseUrl) ||
      req.url.includes(environment.farmConnectBaseUrl)
    ) {
      const localStorageOrgId = window.localStorage.getItem('currentOrganisationId');
      const redirectPath = `${window.location.origin}/callback`;
      const options: GetTokenSilentlyOptions = {
        authorizationParams: {
          audience: environment.apiAudience,
          ...(localStorageOrgId && { organization: localStorageOrgId }),
          redirect_uri: redirectPath,
        },
      };
      return this.auth.getTokenSilently$(options).pipe(
        mergeMap((token: string) => this.createTokenReq(token, req, next)),
        catchError(error =>
          throwError(() => {
            console.error('Interceptor error', error);

            // sometimes after token expiration handling on the callback URL is
            // stuck. This workaround navigates to the dashboard page.
            const url = this.router.url;
            if (error?.statusText?.toLowerCase().includes('unknown error') && url.startsWith('/callback?code=')) {
              void this.router.navigate(['/']);
              return error;
            }

            const errorData = error.error;
            const errorText = typeof errorData === 'string' ? (errorData as string)?.toLowerCase() : errorData.error;
            const errorDescription =
              typeof error.error_description === 'string' ? (error.error_description as string)?.toLowerCase() : null;

            // as seen in #191740 it can occur that an old organization is set in
            // the local storage which then leads to an authorize call using an
            // invalid organisation for the currently logged in user. In that case
            // the user session needs to be refreshed by a re-login
            if (
              (errorText === 'invalid_request' && errorDescription?.includes('parameter organization is invalid')) ||
              (errorText === 'interaction_required' && errorDescription?.includes('external interaction required')) ||
              errorText === 'login_required' ||
              (typeof errorData === 'string' ? errorText.includes('login required') : false)
            ) {
              // call of localStorage.setItem('redirectUrl', url); should not be
              // neccessary because this is done in the auth effects

              // when callback is current route do not relogin
              if (!url.startsWith('/callback?code=')) {
                this.relogin();
              }
            }

            if (errorDescription != null && errorDescription.length > 0) {
              return new Error(errorDescription);
            }

            if (error?.data?.message != null) {
              return new Error(error?.data?.message);
            }

            // handle error with localMessageKey. Do not return errorData which is just a short summary in case of localMessageKey errors
            if (error?.localMessageKey?.length > 0 && error?.status != null) {
              return error;
            }

            return errorData;
          })
        )
      );
    } else {
      req = req.clone({ setHeaders: {} });
      return this.handleRequest(req, next).pipe(
        catchError(error => {
          console.error('Unauthed route interceptor error', error);
          return throwError(() => error);
        })
      );
    }
  }

  clearCache() {
    this.runningRequestUrls.clear();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private createTokenReq(token: string, req: HttpRequest<any>, next: HttpHandler) {
    this.store.dispatch(new AuthActions.SetToken(token));
    const tokenReq = req.clone({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      setHeaders: { Authorization: `Bearer ${token}` },
    });
    return this.handleRequest(tokenReq, next);
  }

  /**
   * Wrapper to handle requests in order to cancel repeated requests
   * @param request the incoming request
   * @param next the httpHandler
   * @returns the obervable http event
   */
  private handleRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const url = request.urlWithParams;
    // only cache GET requests
    if (request.method !== 'GET' || this.isUrlException(url)) {
      return next.handle(request);
    }

    // This is used to bypass the cache. Passing the 'no-cache'
    // param in the request URL will trigger a new request
    const noCacheParam = request.params.get('no-cache');
    const noCacheActive = noCacheParam != null ? !!+noCacheParam : false;
    if (noCacheActive) {
      return next.handle(request);
    }

    // check if the request is already running. If so, return EMPTY to avoid
    // duplicate requests. The set acts as a debouncer of backend requests.
    const requestIsRunningForUrl = this.runningRequestUrls.has(url);
    if (requestIsRunningForUrl) {
      return EMPTY;
    }

    this.runningRequestUrls.add(url);
    return next.handle(request).pipe(
      finalize(() => {
        this.runningRequestUrls.delete(url);
      })
    );
  }

  private isUrlException(url: string): boolean {
    return URL_EXCEPTIONS.some(exception => {
      if (typeof exception === 'string') {
        return url.includes(exception);
      }

      if (Array.isArray(exception)) {
        return exception.every(e => url.includes(e as string));
      }

      if (typeof exception === 'function') {
        return (exception as (url: string) => boolean)(url);
      }

      return false;
    });
  }

  private relogin() {
    this.clearCache();
    this.auth.relogin();
  }
}
